import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ChefHat, Package, Users, MessageSquare,
  CreditCard, Activity,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4">
      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">
        <Icon className={cn("h-3 w-3", accent || "text-emerald-400")} />
        {label}
      </div>
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}

export default function OperationsTab({ registerRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await axios.get(`${API}/admin/metrics/operations`, {
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load operations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (registerRefresh) registerRefresh(() => fetchData);
  }, [registerRefresh, fetchData]);

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin inline" />
      </div>
    );
  }

  if (!data) return null;

  const activeRatio = data.total_recipes > 0
    ? ((data.active_recipes / data.total_recipes) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-6" data-testid="hq-tab-ops">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat icon={Users} label="Total Profiles" value={data.total_users} />
        <Stat icon={ChefHat} label="Total Recipes" value={data.total_recipes} accent="text-amber-400" />
        <Stat icon={Activity} label="Active Recipes" value={`${data.active_recipes} (${activeRatio}%)`} accent="text-emerald-400" />
        <Stat icon={Package} label="Total Ingredients" value={data.total_ingredients} accent="text-sky-400" />
        <Stat icon={MessageSquare} label="Feedback Items" value={data.feedback_total} accent="text-violet-400" />
        <Stat icon={CreditCard} label="Payment Attempts" value={data.payment_attempts} accent="text-rose-400" />
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-5">
        <h3 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-mono mb-3">
          System Health
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-slate-300">Backend reachable</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <span className="text-slate-300">Supabase responsive</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <span className="text-slate-300">RBAC role gate active</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
            <span className="text-slate-300">JWT validation OK</span>
          </div>
        </div>
        <div className="text-[0.65rem] text-slate-600 font-mono mt-4">
          Snapshot taken: {new Date(data.timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
